import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, asArray } from "@/lib/api";
import { toast } from "sonner";
import { KPI, Ring } from "@/components/Primitives";
import { MapPin, Plus, Trash2, Building, Boxes } from "lucide-react";

const EMPTY_BRANCH = { name: "", address: "", city: "", country: "", timezone: "", contact_name: "", contact_phone: "" };

export default function ClientDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState(EMPTY_BRANCH);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [sel, setSel] = useState([]);
  const [savingTpl, setSavingTpl] = useState(false);

  const loadClient = () => api.get(`/clients/${id}`).then(r => { setC(r.data); setSel(r.data?.onboarding_template_ids || []); }).catch(() => setC({}));
  const loadBranches = () => api.get("/sites", { params: { client_id: id } }).then(r => setBranches(asArray(r.data))).catch(() => setBranches([]));
  useEffect(() => { loadClient(); loadBranches(); api.get("/install-templates").then(r => setTemplates(asArray(r.data))).catch(() => setTemplates([])); }, [id]);

  const toggleTpl = (tid) => setSel(s => s.includes(tid) ? s.filter(x => x !== tid) : [...s, tid]);
  const saveTemplates = async () => {
    setSavingTpl(true);
    try { await api.patch(`/clients/${id}`, { onboarding_template_ids: sel }); toast.success("Onboarding software set updated"); }
    catch { toast.error("Failed to save"); } finally { setSavingTpl(false); }
  };

  const addBranch = async () => {
    if (!form.name.trim()) { toast.error("Branch name is required"); return; }
    setSaving(true);
    try {
      await api.post("/sites", { ...form, client_id: id });
      toast.success("Branch added");
      setForm(EMPTY_BRANCH); setAdding(false);
      loadBranches();
    } catch { toast.error("Failed to add branch"); } finally { setSaving(false); }
  };

  const removeBranch = async (bid) => {
    try { await api.delete(`/sites/${bid}`); toast.success("Branch removed"); loadBranches(); }
    catch { toast.error("Failed to remove branch"); }
  };

  if (!c) return <div className="text-zinc-500 text-sm">Loading…</div>;
  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Company</div>
        <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">{c.name}</h1>
        <div className="text-xs text-zinc-500 mt-1">{[c.industry, c.country, c.tier].filter(Boolean).join(" · ")}</div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <div className="bg-zinc-950 border border-zinc-900 rounded-md p-4 flex flex-col items-center"><Ring value={c.health_score ?? 0} label="Health" size={72}/></div>
        <KPI label="Assets" value={c.assets_count ?? 0} testid="cli-assets" />
        <KPI label="Open Tickets" value={c.tickets_open ?? 0} testid="cli-tickets" />
        <KPI label="Compliance" value={c.compliance_findings ?? 0} testid="cli-comp" tone="warn" />
        <KPI label="Security" value={c.security_findings ?? 0} testid="cli-sec" tone="warn" />
        <KPI label="Branches" value={branches.length} testid="cli-branches" />
      </div>

      <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-900 flex items-center justify-between">
          <div className="flex items-center gap-2 font-display font-medium"><Building size={15} className="text-blue-400" /> Branches &amp; Locations</div>
          <button onClick={() => setAdding(v => !v)} data-testid="branch-add-toggle" className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded px-3 py-1.5">
            <Plus size={14} /> Add branch
          </button>
        </div>

        {adding && (
          <div className="px-5 py-4 border-b border-zinc-900 bg-zinc-900/20">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                ["name", "Branch name *"], ["address", "Address"], ["city", "City"],
                ["country", "Country"], ["timezone", "Timezone (e.g. Asia/Manila)"], ["contact_name", "Site contact"],
                ["contact_phone", "Contact phone"],
              ].map(([k, ph]) => (
                <input key={k} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })}
                  placeholder={ph} data-testid={`branch-field-${k}`}
                  className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={addBranch} disabled={saving} data-testid="branch-save" className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm rounded px-4 py-1.5">{saving ? "Saving…" : "Save branch"}</button>
              <button onClick={() => { setAdding(false); setForm(EMPTY_BRANCH); }} className="bg-zinc-900 border border-zinc-800 text-zinc-300 text-sm rounded px-4 py-1.5">Cancel</button>
            </div>
          </div>
        )}

        <table className="w-full text-sm">
          <thead className="bg-zinc-900/50 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
            <tr><th className="text-left px-4 py-2">Branch</th><th className="text-left px-4 py-2">Address</th><th className="text-left px-4 py-2">Timezone</th><th className="text-left px-4 py-2">Contact</th><th className="w-10"></th></tr>
          </thead>
          <tbody>
            {branches.map(b => (
              <tr key={b.id} className="border-b border-zinc-900" data-testid={`branch-row-${b.id}`}>
                <td className="px-4 py-2"><div className="flex items-center gap-2"><MapPin size={13} className="text-zinc-500" />{b.name}</div></td>
                <td className="px-4 py-2 text-zinc-400">{[b.address, b.city, b.country].filter(Boolean).join(", ") || "—"}</td>
                <td className="px-4 py-2 font-mono text-xs text-zinc-500">{b.timezone || "—"}</td>
                <td className="px-4 py-2 text-zinc-400">{b.contact_name || "—"}{b.contact_phone ? ` · ${b.contact_phone}` : ""}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => removeBranch(b.id)} data-testid={`branch-delete-${b.id}`} className="text-zinc-600 hover:text-red-400"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {branches.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-sm text-zinc-500">No branches yet — add this company's other locations to scope endpoints and deployments per site.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-900 flex items-center justify-between">
          <div className="flex items-center gap-2 font-display font-medium"><Boxes size={15} className="text-blue-400" /> Onboarding Software</div>
          <button onClick={saveTemplates} disabled={savingTpl} data-testid="cli-save-templates" className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm rounded px-3 py-1.5">{savingTpl ? "Saving…" : "Save"}</button>
        </div>
        <div className="p-5">
          <p className="text-xs text-zinc-500 mb-3">Templates selected here are installed when a new endpoint is onboarded to this company.</p>
          {templates.length === 0 ? (
            <div className="text-sm text-zinc-500">No install templates yet — create one under <span className="text-zinc-300">Administration → Install Templates</span>.</div>
          ) : (
            <div className="space-y-2">
              {templates.map(t => (
                <label key={t.id} className="flex items-center gap-3 p-2 rounded hover:bg-zinc-900/40 cursor-pointer" data-testid={`cli-tpl-${t.id}`}>
                  <input type="checkbox" checked={sel.includes(t.id)} onChange={() => toggleTpl(t.id)} className="accent-blue-600" />
                  <span className="text-sm text-zinc-200">{t.name}</span>
                  <span className="text-[11px] text-zinc-500 font-mono">{(t.items || []).length} apps</span>
                  {t.description && <span className="text-xs text-zinc-600 truncate">· {t.description}</span>}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
